import { useState } from 'react'
import { Link } from 'react-router'
import { CategoryMark } from '../spaces/CategoryMark'
import { Cadenas } from './Verrou'

export type CardItem = {
  readonly id: string
  readonly title: string
  readonly type: string
  readonly domain: string | null
  readonly imageUrl: string | null
  readonly categoryName: string | null
  readonly categoryInitials: string | null
  readonly categoryHue: number | null
  readonly createdByName: string
  /** Sous verrou : la carte porte un cadenas, et son contenu n'est pas arrivé jusqu'ici. */
  readonly isLocked?: boolean
}

/**
 * Carte de fiche — arbitrage `1g` : bandeau de catégorie, image en haut.
 *
 * Deux formes, et une seule marque de catégorie par carte. La première version en affichait deux —
 * une dans un emplacement d'image vide, une dans le bandeau — ce qui remplissait la carte sans
 * rien dire de plus. Le cahier veut l'inverse : « l'image porte la reconnaissance », et à défaut
 * « l'aplat de sa catégorie et sa marque », au singulier (variante `1i`).
 */
export function ItemCard({ item, to }: { item: CardItem; to: string }) {
  const teinte = item.categoryHue
  const aplat = teinte === null ? 'var(--color-sunken)' : `var(--color-cat-${teinte}-fill)`
  const source = item.domain ?? (item.type === 'note' ? 'note' : 'photo')

  // Une image qui ne vient pas doit libérer sa place.
  //
  // Les vignettes d'Instagram et de Facebook répondent 403 à notre site : la carte réservait un
  // rectangle de 4/3 — près de la moitié de sa hauteur — pour n'y afficher que l'icône d'image
  // cassée du navigateur. Le serveur en garde désormais une copie (décision L113), mais le
  // temps qu'elle arrive, et pour les trois cas sur quatre où le site refuse jusqu'au bout, la
  // carte retombe ici sur sa forme sans image, qui est complète et lisible.
  const [imageCassee, setImageCassee] = useState(false)

  // Avec image : elle occupe le haut, le bandeau nomme la catégorie sous elle.
  if (item.imageUrl && !imageCassee) {
    return (
      <Link
        to={to}
        className="block overflow-hidden rounded-card border border-border bg-card"
        style={{ textDecoration: 'none' }}
      >
        <img
          src={item.imageUrl}
          alt=""
          onError={() => setImageCassee(true)}
          className="w-full object-cover"
          style={{ aspectRatio: '4 / 3' }}
        />

        <div className="flex items-center gap-2 px-3" style={{ background: aplat, paddingBlock: 8 }}>
          {teinte === null ? (
            <span className="text-meta font-semibold text-ink-muted">À classer</span>
          ) : (
            <>
              <CategoryMark initials={item.categoryInitials ?? '??'} hue={teinte} size="small" />
              <span className="text-meta font-semibold">{item.categoryName}</span>
            </>
          )}
          {item.isLocked ? (
            <span className="ml-auto" aria-label="Fiche protégée">
              <Cadenas taille={15} />
            </span>
          ) : null}
        </div>

        <Corps titre={item.title} source={source} auteur={item.createdByName} />
      </Link>
    )
  }

  // Sans image : la carte est l'aplat. Compacte, et le contenu occupe la place au lieu d'un
  // emplacement vide.
  return (
    <Link
      to={to}
      className="block overflow-hidden rounded-card border border-border"
      style={{ background: aplat, textDecoration: 'none' }}
    >
      <div className="flex items-center gap-2 px-3" style={{ paddingTop: 12 }}>
        {teinte === null ? (
          <span className="text-meta font-semibold text-ink-muted">À classer</span>
        ) : (
          <>
            <CategoryMark initials={item.categoryInitials ?? '??'} hue={teinte} size="small" />
            <span className="text-meta font-semibold">{item.categoryName}</span>
          </>
        )}
        {item.isLocked ? (
          <span className="ml-auto" aria-label="Fiche protégée">
            <Cadenas taille={15} />
          </span>
        ) : null}
      </div>

      <Corps titre={item.title} source={source} auteur={item.createdByName} />
    </Link>
  )
}

function Corps({ titre, source, auteur }: { titre: string; source: string; auteur: string }) {
  return (
    <div className="p-3">
      {/* « Titre de carte sur deux lignes maximum » — section 03.3. */}
      <p
        className="text-card-title font-semibold"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {titre}
      </p>
      <p className="mt-1 text-meta" style={{ color: 'var(--color-ink-muted)' }}>
        {source} · {auteur}
      </p>
    </div>
  )
}
